import React, { useState, useMemo } from 'react';
import { Wrench, X, Trash2 } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { PERIODS, periodLabel } from '../../utils/financialPeriods';
import { usePeriodDrilldown } from '../../hooks/usePeriodDrilldown';
import MantleGrid, { MantleGridButton } from '../../components/MantleGrid';
import { cell, GRID } from '../../styles/gridStyles';
import WiringNotes from '../../components/WiringNotes';

// =============================================================================
// Manufacturing › Maintenance Budget — UI MOCKUP (dummy data, no backend yet)
//
// Same grid as the rest of the app (MantleGrid + Full Year → quarter → month
// drill-down). Maintenance-specific: every line carries a maintenance TYPE and,
// crucially, a GL ACCOUNT — the one firm requirement is that this ties into the
// set of GLs we budget against. The "Budgeted GL Accounts" rollup below sums the
// lines per GL; that roll-up is what posts to the budget ledger once wired.
//
//   • Cost Category — editable text.
//   • Type — click the tag to cycle Planned → Reactive → … (analysis only).
//   • GL Acct — click the cell to pick the account (or remove the line).
//   • Months — enter currency per period; shutdowns/inspections are lumpy.
// =============================================================================

const flat = (v) => Array(12).fill(v);

const GL_ACCOUNTS = [
  { code: '7100', name: 'Planned Maintenance — Mechanical' },
  { code: '7110', name: 'Planned Maintenance — Electrical' },
  { code: '7120', name: 'Breakdown / Reactive Repairs' },
  { code: '7130', name: 'Service Contracts' },
  { code: '7140', name: 'Spares & Consumables' },
  { code: '7150', name: 'Lubricants & Oils' },
  { code: '7160', name: 'Calibration & Inspection' },
  { code: '7170', name: 'Shutdown / Turnaround' },
];
const GL_NAME = Object.fromEntries(GL_ACCOUNTS.map((g) => [g.code, g.name]));

const TYPES = ['Planned', 'Reactive', 'Contract', 'Spares', 'Inspection', 'Shutdown'];
// Fixed hex so the tag reads on both the light and dark grid palettes.
const TYPE_TAG = {
  Planned: { activeColor: '#047857', activeBg: '#d1fae5' },
  Reactive: { activeColor: '#b91c1c', activeBg: '#fee2e2' },
  Contract: { activeColor: '#0369a1', activeBg: '#e0f2fe' },
  Spares: { activeColor: '#6d28d9', activeBg: '#ede9fe' },
  Inspection: { activeColor: '#b45309', activeBg: '#fef3c7' },
  Shutdown: { activeColor: '#334155', activeBg: '#e2e8f0' },
};

const DUMMY_ROWS = [
  { id: 'm1', category: 'PM — Production line servicing', type: 'Planned', gl: '7100', months: flat(18000) },
  { id: 'm2', category: 'PM — Electrical & instrumentation', type: 'Planned', gl: '7110', months: flat(12000) },
  { id: 'm3', category: 'Breakdown repairs (reactive)', type: 'Reactive', gl: '7120', months: flat(22000) },
  { id: 'm4', category: 'HVAC & compressor service contract', type: 'Contract', gl: '7130', months: flat(9500) },
  { id: 'm5', category: 'Crane & lifting inspection (statutory)', type: 'Inspection', gl: '7160', months: [0, 0, 8000, 0, 0, 8000, 0, 0, 8000, 0, 0, 8000] },
  { id: 'm6', category: 'Spare parts consumption', type: 'Spares', gl: '7140', months: flat(15000) },
  { id: 'm7', category: 'Lubricants & oils', type: 'Spares', gl: '7150', months: flat(4200) },
  { id: 'm8', category: 'Annual shutdown overhaul', type: 'Shutdown', gl: '7170', months: [0, 0, 0, 0, 0, 0, 320000, 0, 0, 0, 0, 0] },
  { id: 'm9', category: 'Calibration (QA instruments)', type: 'Inspection', gl: '7160', months: flat(1800) },
];

const rowTotal = (r) => r.months.reduce((a, v) => a + (Number(v) || 0), 0);
const fmtR = (n) => `R ${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

let nextId = 100;

const MaintenanceBudget = () => {
  const { fyStartMonth } = useAppContext();
  const [rows, setRows] = useState(() => DUMMY_ROWS.map((r) => ({ ...r, months: [...r.months] })));
  const [dirty, setDirty] = useState(false);
  const [glPickerRow, setGlPickerRow] = useState(null); // row id whose GL picker is open

  const {
    visibleCols, yearExpanded, expandedQuarters,
    toggleYear, toggleQuarter, expandAll, collapseAll,
  } = usePeriodDrilldown(PERIODS);

  const sumPeriods = (row, ps) => ps.reduce((a, p) => a + (Number(row.months[p - 1]) || 0), 0);

  // ---- Columns (same base + drill-down as BudgetGrid / Fixed Costs) ----
  const columns = useMemo(() => {
    const base = [
      { columnId: 'category', width: 240, resizable: true },
      { columnId: 'type', width: 112, resizable: true },
      { columnId: 'gl', width: 84, resizable: true },
    ];
    const dyn = visibleCols.map((col) => {
      if (col.kind === 'year') return { columnId: 'YEAR', width: 130, resizable: true };
      if (col.kind === 'quarter') return { columnId: `Q${col.q}`, width: 110, resizable: true };
      return { columnId: `P${col.period}`, width: 84, resizable: true };
    });
    return [...base, ...dyn];
  }, [visibleCols]);

  const gridRows = useMemo(() => {
    const headerRow = {
      rowId: 'header',
      height: GRID.size.headerRow,
      cells: [
        cell.header('Cost Category'),
        cell.header('Type'),
        cell.header('GL Acct'),
        ...visibleCols.map((col) => {
          if (col.kind === 'year') return cell.drillHeader(`${yearExpanded ? '▾' : '▸'} Full Year`, 'year');
          if (col.kind === 'quarter') return cell.drillHeader(`${expandedQuarters[col.q] ? '▾' : '▸'} Q${col.q}`, 'quarter');
          return cell.header(periodLabel(col.period, fyStartMonth), { align: 'right' });
        }),
      ],
    };

    const dataRows = rows.map((r, idx) => {
      const tt = TYPE_TAG[r.type] || {};
      const dyn = visibleCols.map((col) => {
        if (col.kind === 'year' || col.kind === 'quarter') {
          return cell.aggregate(sumPeriods(r, col.periods), { kind: col.kind });
        }
        return cell.number(Number(r.months[col.period - 1]) || 0, { editable: true, idx });
      });
      return {
        rowId: r.id,
        height: GRID.size.dataRow,
        cells: [
          cell.editText(r.category, { idx }),
          cell.tag(r.type, { active: true, activeColor: tt.activeColor, activeBg: tt.activeBg, clickable: true }),
          cell.tag(r.gl, { active: true, activeColor: '#1e293b', activeBg: '#e2e8f0', clickable: true }),
          ...dyn,
        ],
      };
    });

    const totalRow = {
      rowId: 'totals',
      height: GRID.size.totalRow,
      cells: [
        cell.totalLabel('TOTAL'),
        cell.totalBlank(),
        cell.totalBlank(),
        ...visibleCols.map((col) => {
          const ps = col.kind === 'month' ? [col.period] : col.periods;
          return cell.totalNumber(rows.reduce((a, r) => a + sumPeriods(r, ps), 0));
        }),
      ],
    };

    return [headerRow, ...dataRows, totalRow];
  }, [rows, visibleCols, yearExpanded, expandedQuarters, fyStartMonth]);

  const handleFocus = (loc) => {
    if (loc.rowId === 'header') {
      const colId = String(loc.columnId);
      if (colId === 'YEAR') { toggleYear(); return false; }
      if (colId.startsWith('Q')) { toggleQuarter(Number(colId.slice(1))); return false; }
      return true;
    }
    if (loc.rowId === 'totals') return true;
    const colId = String(loc.columnId);
    // Click the Type tag → cycle to the next type.
    if (colId === 'type') {
      setRows((prev) => prev.map((r) => (r.id === loc.rowId
        ? { ...r, type: TYPES[(TYPES.indexOf(r.type) + 1) % TYPES.length] }
        : r)));
      setDirty(true);
      return false;
    }
    // Click the GL cell → open the account picker for that line.
    if (colId === 'gl') { setGlPickerRow(loc.rowId); return false; }
    return true;
  };

  const handleChanges = (changes) => {
    setRows((prev) => {
      const next = prev.map((r) => ({ ...r, months: [...r.months] }));
      for (const ch of changes) {
        if (ch.rowId === 'header' || ch.rowId === 'totals') continue;
        const colId = String(ch.columnId);
        const row = next.find((r) => r.id === ch.rowId);
        if (!row) continue;
        if (colId === 'category') {
          row.category = ch.newCell?.text ?? row.category;
        } else if (colId.startsWith('P')) {
          const i = Number(colId.slice(1)) - 1;
          const val = Number(ch.newCell?.value);
          if (!isNaN(val) && i >= 0 && i < 12) row.months[i] = val;
        }
      }
      return next;
    });
    setDirty(true);
  };

  const addLine = () => {
    setRows((prev) => [...prev, { id: `n${nextId++}`, category: '', type: 'Planned', gl: GL_ACCOUNTS[0].code, months: flat(0) }]);
    setDirty(true);
  };
  const setGl = (id, gl) => { setRows((prev) => prev.map((r) => (r.id === id ? { ...r, gl } : r))); setDirty(true); };
  const removeLine = (id) => { setRows((prev) => prev.filter((r) => r.id !== id)); setDirty(true); setGlPickerRow(null); };

  // ---- Rollups ----
  const monthTotals = useMemo(() => PERIODS.map((_, i) => rows.reduce((a, r) => a + (Number(r.months[i]) || 0), 0)), [rows]);
  const annual = useMemo(() => monthTotals.reduce((a, v) => a + v, 0), [monthTotals]);
  const glRollup = useMemo(() => {
    const by = new Map();
    for (const r of rows) by.set(r.gl, (by.get(r.gl) || 0) + rowTotal(r));
    return [...by.entries()]
      .map(([code, total]) => ({ code, name: GL_NAME[code] || code, total }))
      .sort((a, b) => a.code.localeCompare(b.code));
  }, [rows]);
  const typeTotals = useMemo(() => {
    const by = {};
    for (const r of rows) by[r.type] = (by[r.type] || 0) + rowTotal(r);
    return by;
  }, [rows]);

  const pickerRow = rows.find((r) => r.id === glPickerRow) || null;

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      {/* Header + summary */}
      <div className="shrink-0 px-6 pt-6 pb-4">
        <h1 className="m-0 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100 flex items-center gap-2">
          <Wrench className="w-5 h-5 text-slate-400" /> Manufacturing · Maintenance Budget
          <span className="ml-1 inline-block px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400">
            Mockup · dummy data
          </span>
        </h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Budget factory maintenance by line, phased across the year. Every line ties to a GL account — the rollup below is what posts to the budget ledger.
        </p>

        <div className="mt-4 flex flex-wrap gap-3">
          {[
            ['Annual maintenance', fmtR(annual)],
            ['GL accounts used', String(glRollup.length)],
            ['Planned + Preventive', fmtR((typeTotals.Planned || 0) + (typeTotals.Inspection || 0))],
            ['Reactive / breakdown', fmtR(typeTotals.Reactive || 0)],
          ].map(([label, value]) => (
            <div key={label} className="px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 min-w-[11rem]">
              <p className="m-0 text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">{label}</p>
              <p className="m-0 mt-1 text-lg font-bold font-mono text-slate-800 dark:text-slate-100">{value}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="flex-1 min-h-0 px-6 pb-6 overflow-y-auto space-y-4">
        {/* Grid — same structure as the Opex / Fixed Costs grids */}
        <div
          className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden"
          style={{ height: GRID.size.headerRow + rows.length * GRID.size.dataRow + GRID.size.totalRow + 44 }}
        >
          <MantleGrid
            rows={gridRows}
            columns={columns}
            onCellsChanged={handleChanges}
            onFocusLocationChanging={handleFocus}
            hint="Drill Full Year → quarters → months. Edit months directly; click the Type tag to cycle it, or the GL cell to pick the account."
            toolbar={
              <>
                <MantleGridButton onClick={addLine}>+ Add line</MantleGridButton>
                <MantleGridButton onClick={expandAll}>Expand all</MantleGridButton>
                <MantleGridButton onClick={collapseAll}>Collapse all</MantleGridButton>
                <button
                  onClick={() => setDirty(false)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-md text-white shadow-sm transition-colors ${
                    dirty ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-400 dark:bg-slate-600'
                  }`}
                >
                  Save
                </button>
              </>
            }
          />
        </div>

        {/* GL rollup — the set of GLs being budgeted against */}
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 flex items-center justify-between">
            <div>
              <span className="text-xs font-bold tracking-wider text-slate-500 dark:text-slate-400 uppercase">Budgeted GL Accounts</span>
              <p className="m-0 mt-0.5 text-xs text-slate-400">Every line rolls up into its GL account — this is what posts to the budget ledger.</p>
            </div>
            <span className="text-sm font-bold font-mono text-slate-800 dark:text-slate-100">{fmtR(annual)}</span>
          </div>
          <div className="divide-y divide-slate-100 dark:divide-slate-700/60">
            {glRollup.map((g) => (
              <div key={g.code} className="px-4 py-2.5 flex items-center gap-3">
                <span className="font-mono text-xs font-bold text-slate-700 dark:text-slate-200 w-14">{g.code}</span>
                <span className="text-sm text-slate-600 dark:text-slate-300 flex-1 truncate">{g.name}</span>
                <div className="w-40 h-2 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                  <div className="h-full bg-emerald-500/70" style={{ width: `${annual ? (g.total / annual) * 100 : 0}%` }} />
                </div>
                <span className="w-16 text-right text-[11px] text-slate-400">{annual ? Math.round((g.total / annual) * 100) : 0}%</span>
                <span className="w-28 text-right font-mono text-sm font-semibold text-slate-800 dark:text-slate-100">{fmtR(g.total)}</span>
              </div>
            ))}
          </div>
        </div>

        <WiringNotes
          notes={[
            'GL is the anchor: every line carries account_code (a picker from md_gl_accounts, not free text). The "Budgeted GL Accounts" rollup is the sum per account per period and is exactly what posts to the budget ledger.',
            'Wire the roll-up into the existing budget tables: each (line, period) becomes a bud_opex_lines row under a calculated bud_gl_lines for that account_code + cost_center_code + site_code + version_id; the existing opex→GL sync trigger then maintains bud_gl_periods (same mechanism the Opex build uses).',
            'Alternatively, if maintenance needs no P×Q detail, aggregate the 12 monthly line values straight into bud_gl_periods per account. Either way the GL account is the join key back into budget-vs-actual reporting.',
            'Type (Planned / Reactive / Contract / Spares / Inspection / Shutdown) is an analysis dimension only — store it on the line for reporting; it does not change the GL posting.',
            'Scope by budget_version_id + site_code (and cost_center_code / work_center for factory areas). Phasing is stored as the 12 monthly values so shutdowns and quarterly inspections land in the right month.',
          ]}
          routes={`// Node/Express — routes/manufacturingRoutes.js
GET  /api/manufacturing/maintenance      ?version=&site=   -> lines (category, type, account_code, months[12])
PUT  /api/manufacturing/maintenance                         -> upsert lines
GET  /api/manufacturing/maintenance/gl-rollup ?version=&site= -> Σ per account_code per period
// account_code options come from the existing GL master (md_gl_accounts).`}
          schema={`-- PostgreSQL
CREATE TABLE bud_maintenance (
  version_id   varchar(50) NOT NULL,
  site_code    varchar(50) NOT NULL,
  line_id      uuid DEFAULT gen_random_uuid() PRIMARY KEY,
  category     varchar(255) NOT NULL,
  maint_type   varchar(20)  NOT NULL,                 -- Planned/Reactive/Contract/Spares/Inspection/Shutdown
  account_code varchar(50)  NOT NULL,                 -- -> md_gl_accounts.account_code  (the GL tie-in)
  cost_center_code varchar(50),
  created_at timestamptz DEFAULT now(), updated_at timestamptz DEFAULT now()
);
CREATE TABLE bud_maintenance_periods (
  line_id uuid REFERENCES bud_maintenance(line_id) ON DELETE CASCADE,
  financial_period int NOT NULL CHECK (financial_period BETWEEN 1 AND 12),
  amount numeric(19,4) NOT NULL DEFAULT 0,
  PRIMARY KEY (line_id, financial_period)
);
-- Budget ledger view: what we budget against, per GL account per period.
CREATE VIEW vw_maintenance_gl AS
  SELECT m.version_id, m.site_code, m.account_code, p.financial_period,
         SUM(p.amount) AS budget_amount
  FROM bud_maintenance m JOIN bud_maintenance_periods p USING (line_id)
  GROUP BY m.version_id, m.site_code, m.account_code, p.financial_period;`}
        />
      </div>

      {/* GL account picker (opened from a line's GL cell) */}
      {pickerRow && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setGlPickerRow(null); }}
        >
          <div className="w-full max-w-md rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-2xl">
            <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/95 flex items-start justify-between gap-3">
              <div>
                <h3 className="m-0 text-sm font-bold text-slate-800 dark:text-slate-100">GL account</h3>
                <p className="m-0 mt-0.5 text-xs text-slate-500 dark:text-slate-400 truncate">{pickerRow.category || 'New line'}</p>
              </div>
              <button type="button" onClick={() => setGlPickerRow(null)} className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700" aria-label="Close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-3 max-h-80 overflow-y-auto">
              {GL_ACCOUNTS.map((g) => {
                const on = pickerRow.gl === g.code;
                return (
                  <button
                    key={g.code}
                    type="button"
                    onClick={() => { setGl(pickerRow.id, g.code); setGlPickerRow(null); }}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                      on ? 'bg-emerald-50 dark:bg-emerald-500/10 ring-1 ring-emerald-500/40' : 'hover:bg-slate-50 dark:hover:bg-slate-700/40'
                    }`}
                  >
                    <span className="font-mono text-xs font-bold text-slate-700 dark:text-slate-200 w-12">{g.code}</span>
                    <span className="text-sm text-slate-600 dark:text-slate-300 flex-1">{g.name}</span>
                    {on && <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">current</span>}
                  </button>
                );
              })}
            </div>
            <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-between">
              <button
                type="button"
                onClick={() => removeLine(pickerRow.id)}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-red-600 dark:text-red-400 hover:underline"
              >
                <Trash2 className="w-3.5 h-3.5" /> Remove line
              </button>
              <button type="button" onClick={() => setGlPickerRow(null)} className="px-4 py-2 text-sm font-medium rounded-md border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700">
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaintenanceBudget;
